import React, { useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, ShieldCheck, ArrowLeft } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      toast.error("Passwords don't match");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, new_password: password });
      toast.success(data.message || "Password reset");
      navigate("/login");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background afha-grain flex flex-col" data-testid="reset-password-page">
      <header className="bg-white border-b border-border">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 h-[68px] flex items-center">
          <Logo />
        </div>
      </header>
      <main className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md bg-white border border-border rounded-xl p-8">
          <div className="h-12 w-12 rounded-lg bg-navy flex items-center justify-center mb-5">
            <ShieldCheck className="h-6 w-6 text-white" />
          </div>
          <h1 className="font-display text-2xl text-navy tracking-tight">Set a new password</h1>
          {!token ? (
            <p className="mt-3 text-sm text-patriot" data-testid="reset-no-token">
              This reset link is missing or invalid. Please request a new one from the{" "}
              <Link to="/forgot-password" className="font-semibold underline">forgot password</Link> page.
            </p>
          ) : (
            <>
              <p className="mt-2 text-muted-foreground text-sm">Choose a new password for your AFHA account.</p>
              <form onSubmit={submit} className="mt-6 space-y-5" data-testid="reset-form">
                <div>
                  <Label htmlFor="pw" className="text-navy">New password</Label>
                  <Input id="pw" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} data-testid="reset-password" className="mt-1.5 h-11" placeholder="At least 6 characters" />
                </div>
                <div>
                  <Label htmlFor="pw2" className="text-navy">Confirm new password</Label>
                  <Input id="pw2" type="password" required minLength={6} value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="reset-confirm" className="mt-1.5 h-11" placeholder="Re-enter password" />
                </div>
                <Button type="submit" disabled={loading} data-testid="reset-submit" className="w-full h-11 rounded-full bg-navy hover:bg-navy-dark text-white">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Reset password"}
                </Button>
              </form>
            </>
          )}
          <Link to="/login" className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-patriot hover:underline" data-testid="back-to-login">
            <ArrowLeft className="h-4 w-4" /> Back to sign in
          </Link>
        </div>
      </main>
    </div>
  );
}
